import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, X, Trash2, Pencil,
  ClipboardCheck, Building2, CalendarDays,
} from 'lucide-react';
import GridMes, { TagDia } from '../../home/GridMes';
import { MESES, modalOverlay, modalCard, modalHeader, btnX, rotuloStyle, inputStyle, authHeaders } from '../../home/calendario-ui';
import { FONTE } from '../../home/ui';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// =====================================================================
// MANUTENÇÃO — Calendário de programação
// =====================================================================
// Mostra QUANDO a Certificação de cada equipamento e a Conservação Predial de
// cada loja VÃO acontecer — e, junto, se já aconteceram.
//
// ⚠️ ONDE ELE MORA mudou em 2026-09-03: era UM calendário na Visão Geral do
// módulo, com os dois assuntos no mesmo grid; passou a ser um por FRENTE do hub
// (Certificação dentro de "Manutenção Equipamento", Conservação dentro de
// "Conservação"), pela prop `tipo`. Sem `tipo` ele volta a ser o dos dois — e
// esse modo continua inteiro de propósito, não é código morto: é o que serve
// uma leitura de rede, se um dia ela voltar à Visão Geral.
//
// ⚠️ A SITUAÇÃO VEM PRONTA DO BACKEND. "Previsto / Em andamento / Atrasado /
//    Realizado" e os dias que cada período ocupa saem de `agenda-regras.js`
//    (motor puro, testado) e chegam resolvidos no JSON. Esta tela não recalcula
//    nada disso — se recalculasse, a mesma regra passaria a existir nos dois
//    lados e começaria a divergir em silêncio (foi o que aconteceu com os pesos
//    dos formulários de qualidade).
//
// REUSA `GridMes`/`TagDia` da Home: são peças puramente visuais, sem chamada de
// API, já compartilhadas pela tela de aniversariantes e pelo modal do sino de
// calendário. Um terceiro grid seria a mesma matemática de células copiada de
// novo.
//
// MACRO × MICRO: é o seletor de loja. "Todas as lojas" = a rede (a tag mostra
// `LOJA · assunto`); uma loja escolhida = o micro (a tag mostra só o assunto).
// Para o Gerente, o backend devolve apenas a loja dele — não há macro a
// oferecer, e o seletor reflete isso sozinho.
// =====================================================================

const COR_MODULO = '#4f46e5';

// Quem CADASTRA a programação. ⚠️ Espelha o PERFIS_GESTAO do
// server/manutencao/agenda/server-agenda.js — oferecer um botão que voltaria 403 é o
// erro clássico. O Gerente lê o calendário, não agenda.
const PERFIS_GESTAO = ['Admin', 'Manutencao', 'Diretoria'];

// ---------------------------------------------------------------------
// DUAS identidades visuais, cada uma no seu lugar (decidido em 2026-08-25):
//   • no GRID, a cor é do TIPO — Certificação (laranja) × Conservação (azul).
//     É o que se pede ao olho num calendário cheio: separar assunto de assunto.
//   • fora do grid, a cor segue sendo da SITUAÇÃO — o placar do topo, o bloco
//     de passivo, o resumo agrupado e a pastilha do detalhe.
// ⚠️ O grid nasceu colorido por situação justamente para o atraso não se
// esconder. A troca só é aceitável porque o atrasado continua gritando nos
// quatro lugares acima (e o passivo nem obedece à navegação de mês); se um dia
// esses blocos saírem da tela, a cor do atraso volta para cá.
// ---------------------------------------------------------------------
const SITUACAO_VISUAL = {
  'Previsto': { cor: '#475569', fundo: '#e2e8f0', rotulo: 'Previsto' },
  'Em andamento': { cor: '#4338ca', fundo: '#e0e7ff', rotulo: 'Em andamento' },
  'Atrasado': { cor: '#b91c1c', fundo: '#fee2e2', rotulo: 'Atrasado' },
  'Realizado': { cor: '#15803d', fundo: '#dcfce7', rotulo: 'Realizado' },
};
const ORDEM_SITUACAO = ['Atrasado', 'Em andamento', 'Previsto', 'Realizado'];

// A cor de cada assunto do calendário. ⚠️ A tag do grid usa a cor CHEIA com
// texto branco (e não fundo claro com texto colorido): a 11px, cor clara sobre
// tinta clara fica fraca de contraste — cheia, os dois assuntos se separam de
// longe, que é o motivo da cor existir aqui.
// ⚠️ A certificação passou por laranja `#f7613c` (o par da Pesquisa de Escala) e
// âmbar `#b45309` antes de parar no GRAFITE, escolhido por comparação lado a
// lado: num mês cheio a tela fica coberta de tags, e cor quente puxava o olho
// para si em vez de para o que está atrasado. Aqui a certificação é a maioria
// das tags — logo é ela que precisa ser a cor sóbria, e o teal da conservação
// destaca quem é minoria. O assunto continua distinguível pelo ÍCONE.
// ⚠️ O grafite é vizinho do slate de 'Previsto' no placar (#475569), e isso é
// aceitável porque as duas escalas não se cruzam: cor de tipo só existe dentro
// do grid, cor de situação só fora dele. Misturar as duas na mesma peça é o que
// não se pode fazer.
const TIPO_VISUAL = {
  Certificacao: { icone: ClipboardCheck, rotulo: 'Certificação de Equipamento', cor: '#334155' },
  Conservacao: { icone: Building2, rotulo: 'Conservação Predial', cor: '#0a8097' },
};
const ORDEM_TIPO = ['Certificacao', 'Conservacao'];

// dd/mm/aaaa a partir de 'AAAA-MM-DD' (o backend já manda o dia puro).
const dataBR = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
// A data como a coluna DATE chega do MySQL pode vir com hora; o <input type=date>
// só aceita 'AAAA-MM-DD'.
const soDia = (v) => (v ? String(v).slice(0, 10) : '');

const FORM_VAZIO = { id: null, tipo: 'Certificacao', loja: '', idEquipamento: '', dataInicio: '', dataFim: '', observacao: '' };

// Uma linha do resumo — loja · assunto · período · a coluna do que falta saber.
// Extraída porque o bloco de PASSIVO (vencidos de meses anteriores) desenha
// exatamente a mesma linha: duplicá-la faria as duas listas divergirem no
// primeiro ajuste de coluna.
function LinhaResumo({ item, cor, rotuloAssunto, onClick, primeira }) {
  const tipo = TIPO_VISUAL[item.TIPO] || TIPO_VISUAL.Certificacao;
  const Icone = tipo.icone;
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        padding: '10px 14px', cursor: 'pointer', fontSize: '13px',
        borderTop: primeira ? 'none' : '1px solid #f1f5f9',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {/* O ícone é o marcador do tipo — logo carrega a cor do tipo, igual à do
          grid; a cor da SITUAÇÃO fica na coluna da direita e na moldura. */}
      <Icone size={15} color={tipo.cor} style={{ flexShrink: 0 }} />
      {/* Largura fixa na loja para as siglas alinharem coluna a coluna — com
          larguras variáveis a lista vira escada. */}
      <strong style={{ minWidth: '46px', color: '#0f172a' }}>{item.LOJA}</strong>
      <span style={{ flex: 1, minWidth: '160px', color: '#334155' }}>{rotuloAssunto(item)}</span>
      <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {dataBR(soDia(item.DATA_INICIO))} a {dataBR(soDia(item.DATA_FIM))}
      </span>
      {/* A coluna da direita diz o que FALTA saber em cada situação: atraso em
          dias, ou quando foi feito. */}
      <span style={{
        minWidth: '150px', textAlign: 'right', fontWeight: 700,
        color: cor, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      }}>
        {item.situacao === 'Realizado'
          ? `Feito em ${dataBR(item.realizacao?.data)}${item.emAtraso ? ` (+${item.diasAtraso}d)` : ''}`
          : (item.diasAtraso ? `${item.diasAtraso} dia(s) de atraso` : '')}
      </span>
    </div>
  );
}

// Uma linha do painel de tempos: assunto · nº de agendamentos · previsto · real.
function LinhaTempo({ grupo }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap',
      padding: '8px 0', borderTop: '1px solid #f1f5f9', fontSize: '13px',
    }}>
      <strong style={{ flex: 1, minWidth: '150px', color: '#0f172a' }}>{grupo.rotulo}</strong>
      <span style={{ color: '#94a3b8', fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>
        {grupo.agendamentos}× programado{grupo.agendamentos > 1 ? 's' : ''}
      </span>
      <span style={{ minWidth: '112px', textAlign: 'right', fontWeight: 800, color: COR_MODULO, fontVariantNumeric: 'tabular-nums' }}>
        {grupo.diasPrevistos} dia{grupo.diasPrevistos === 1 ? '' : 's'}
      </span>
      {/* ⚠️ O realizado vem com a CONTAGEM colada ("11d em 1 de 3"). Sem ela,
          "14 previstos × 11 realizados" se lê como economia de 3 dias — quando
          na verdade são 3 agendamentos contra 1, porque a duração real só
          existe onde o relatório/programa foi fechado. */}
      <span style={{
        minWidth: '150px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
        color: grupo.comDadoDeDuracao ? '#15803d' : '#cbd5e1', fontWeight: grupo.comDadoDeDuracao ? 700 : 400,
      }}>
        {grupo.comDadoDeDuracao
          ? `${grupo.diasRealizados}d reais em ${grupo.comDadoDeDuracao} de ${grupo.agendamentos}`
          : 'sem fechamento ainda'}
      </span>
    </div>
  );
}

// `tipo` = 'Certificacao' | 'Conservacao' | null.
//   • null  → a agenda dos dois programas no mesmo grid (como nasceu).
//   • fixo  → o calendário de UM programa, que é como ele passou a ser usado
//     em 2026-09-03: cada frente do hub tem o seu.
// ⚠️ Não é só um filtro: com um único assunto na tela, a cor do TIPO no grid
// deixa de informar (era ela que separava certificação de conservação) e a cor
// volta a ser da SITUAÇÃO, como antes de 25/08 — é o que faz o atraso gritar
// dentro do grid. Ver a nota das duas escalas de cor mais acima.
function CalendarioManutencao({ tipo = null }) {
  const tipoFixo = TIPO_VISUAL[tipo] ? tipo : null; // ignora valor inesperado
  const IconeTipoFixo = tipoFixo ? TIPO_VISUAL[tipoFixo].icone : null;
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const podeGerir = PERFIS_GESTAO.includes(perfilNoModulo(user?.perfil, 'manutencao'));

  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);      // 1..12
  const [loja, setLoja] = useState('');                     // '' = macro (todas)

  const [itens, setItens] = useState([]);
  // O passivo (vencidos ANTES do mês exibido) e o total de dias por assunto no
  // ano vêm na MESMA resposta — são leituras do mesmo conjunto, não buscas
  // extras. Ver o cabeçalho do GET / em server-agenda.js.
  const [atrasadosAnteriores, setAtrasadosAnteriores] = useState([]);
  const [tempos, setTempos] = useState({ certificacao: [], conservacao: [] });
  const [lojas, setLojas] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [detalhe, setDetalhe] = useState(null);             // agendamento aberto
  const [form, setForm] = useState(null);                   // null = modal fechado
  const [salvando, setSalvando] = useState(false);

  // Limites do mês exibido — é a janela que o backend recorta (e a partir da
  // qual ele devolve os dias de cada período).
  const primeiroDia = `${ano}-${String(mes).padStart(2, '0')}-01`;
  const ultimoDia = `${ano}-${String(mes).padStart(2, '0')}-${String(new Date(ano, mes, 0).getDate()).padStart(2, '0')}`;

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      // O recorte de tipo é feito no BACKEND (e não com um filter aqui): ele
      // vale de uma vez para as três leituras da resposta — grid, passivo e
      // tempos do ano —, que é onde um filtro no front esqueceria alguma.
      const url = `/api/manutencao/agenda?de=${primeiroDia}&ate=${ultimoDia}`
        + (loja ? `&loja=${encodeURIComponent(loja)}` : '')
        + (tipoFixo ? `&tipo=${tipoFixo}` : '');
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) throw new Error('Não foi possível carregar a programação.');
      const dados = await r.json();
      setItens(dados.itens || []);
      setAtrasadosAnteriores(dados.atrasadosAnteriores || []);
      setTempos(dados.tempos || { certificacao: [], conservacao: [] });
    } catch (e) {
      console.error('[Manutenção/calendário]', e);
      setItens([]);
      setAtrasadosAnteriores([]);
      setTempos({ certificacao: [], conservacao: [] });
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [primeiroDia, ultimoDia, loja, tipoFixo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Lojas e equipamentos não variam com o mês — carga única.
  useEffect(() => {
    (async () => {
      try {
        const [rl, re] = await Promise.all([
          fetch('/api/manutencao/agenda/lojas', { headers: authHeaders() }),
          fetch('/api/manutencao/agenda/equipamentos', { headers: authHeaders() }),
        ]);
        if (rl.ok) setLojas(await rl.json());
        if (re.ok) setEquipamentos(await re.json());
      } catch (e) {
        console.error('[Manutenção/calendário] opções:', e);
      }
    })();
  }, []);

  // Agrupa por dia do mês. Cada agendamento aparece em TODOS os dias que
  // cobre — é o que faz "período" ser legível num grid de dias.
  const porDia = useMemo(() => {
    const mapa = {};
    itens.forEach((it) => {
      (it.dias || []).forEach((d) => {
        const n = Number(d.slice(8, 10));
        (mapa[n] = mapa[n] || []).push(it);
      });
    });
    // Dentro do dia: o que exige ação primeiro (atrasado antes de realizado).
    Object.values(mapa).forEach((lista) =>
      lista.sort((a, b) => ORDEM_SITUACAO.indexOf(a.situacao) - ORDEM_SITUACAO.indexOf(b.situacao))
    );
    return mapa;
  }, [itens]);

  // Placar do mês — a leitura de monitoramento, sem precisar varrer o grid.
  const placar = useMemo(() => {
    const c = { 'Atrasado': 0, 'Em andamento': 0, 'Previsto': 0, 'Realizado': 0 };
    itens.forEach((it) => { c[it.situacao] = (c[it.situacao] || 0) + 1; });
    return c;
  }, [itens]);

  // Resumo abaixo do calendário: os mesmos itens, agrupados por situação e em
  // ordem de início. Não é outra busca — é a mesma lista que o grid pinta.
  //
  // Por que existe: o grid responde "o que acontece no dia 12?"; ele NÃO
  // responde "o que está atrasado?" sem varrer 30 células com o olho. Um
  // agendamento de duas semanas ocupa 14 quadradinhos e, num mês cheio, some
  // no meio dos outros. O resumo é a leitura de cobrança — cada linha uma vez.
  const resumo = useMemo(() => (
    ORDEM_SITUACAO
      .map((situacao) => ({
        situacao,
        itens: itens
          .filter((it) => it.situacao === situacao)
          .sort((a, b) => soDia(a.DATA_INICIO).localeCompare(soDia(b.DATA_INICIO))),
      }))
      // Grupo vazio não vira seção: "Atrasado (0)" é ruído numa tela cujo
      // objetivo é destacar o que precisa de ação. A contagem zerada continua
      // visível nas pastilhas do topo, que também são a legenda das cores.
      .filter((g) => g.itens.length)
  ), [itens]);

  const irMes = (passo) => {
    const d = new Date(ano, mes - 1 + passo, 1);
    setAno(d.getFullYear());
    setMes(d.getMonth() + 1);
  };

  const rotuloAssunto = (it) =>
    it.TIPO === 'Conservacao' ? 'Conservação Predial' : (it.EQUIPAMENTO || 'Equipamento');

  const abrirNovo = () => setForm({
    ...FORM_VAZIO,
    // ⚠️ Num calendário de um programa só, o tipo do novo agendamento é o DELE.
    // Sem isto o formulário abriria em 'Certificacao' dentro da Conservação e
    // gravaria uma linha que aquela tela não mostra — sumindo sem erro nenhum.
    tipo: tipoFixo || FORM_VAZIO.tipo,
    // Em micro, a loja já vem escolhida; em macro, a única loja da lista (o
    // caso do Gerente, se um dia ele puder agendar).
    loja: loja || (lojas.length === 1 ? lojas[0] : ''),
    dataInicio: primeiroDia,
    dataFim: primeiroDia,
  });

  const abrirEdicao = (it) => {
    setDetalhe(null);
    setForm({
      id: it.ID,
      tipo: it.TIPO,
      loja: it.LOJA,
      idEquipamento: it.ID_EQUIPAMENTO || '',
      dataInicio: soDia(it.DATA_INICIO),
      dataFim: soDia(it.DATA_FIM),
      observacao: it.OBSERVACAO || '',
    });
  };

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      const metodo = form.id ? 'PUT' : 'POST';
      const url = form.id ? `/api/manutencao/agenda/${form.id}` : '/api/manutencao/agenda';
      const r = await fetch(url, {
        method: metodo,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível salvar.');
      setForm(null);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (it) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Remover a programação de ${rotuloAssunto(it)} da ${it.LOJA}?`)) return;
    try {
      const r = await fetch(`/api/manutencao/agenda/${it.ID}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) throw new Error('Não foi possível remover.');
      setDetalhe(null);
      carregar();
    } catch (e) {
      setErro(e.message);
    }
  };

  const renderDia = (dia) => (
    <>
      {(porDia[dia] || []).map((it, i) => {
        const tipo = TIPO_VISUAL[it.TIPO] || TIPO_VISUAL.Certificacao;
        const Icone = tipo.icone;
        // Em macro a loja precisa estar na tag (é o que distingue as linhas);
        // em micro ela é redundante e só rouba espaço da célula.
        const texto = loja ? rotuloAssunto(it) : `${it.LOJA} · ${rotuloAssunto(it)}`;
        // ⚠️ Com um assunto só na tela, a cor do TIPO não distingue nada — todas
        // as tags ficariam da mesma cor. A tag volta então a ser da SITUAÇÃO,
        // que é o que o olho precisa achar dentro do grid (o desenho anterior a
        // 25/08). Com os dois assuntos juntos, a cor segue sendo do tipo.
        const sit = SITUACAO_VISUAL[it.situacao] || SITUACAO_VISUAL['Previsto'];
        return (
          <TagDia
            key={`${it.ID}-${i}`}
            cor={tipoFixo ? sit.cor : '#ffffff'}
            fundo={tipoFixo ? sit.fundo : tipo.cor}
            texto={texto}
            title={`${it.LOJA} — ${rotuloAssunto(it)} · ${tipo.rotulo} · ${it.situacao} · ${dataBR(soDia(it.DATA_INICIO))} a ${dataBR(soDia(it.DATA_FIM))}`}
            icone={<Icone size={12} style={{ flexShrink: 0, marginTop: '1px' }} />}
            onClick={() => setDetalhe(it)}
          />
        );
      })}
    </>
  );

  const botaoMes = {
    background: '#fff', border: '1px solid #e0e7ff', borderRadius: '8px',
    padding: '6px 8px', cursor: 'pointer', color: COR_MODULO, display: 'flex', alignItems: 'center',
  };

  return (
    <div style={{ fontFamily: FONTE }}>
      {/* ---------------- Cabeçalho: mês, recorte e ação ---------------- */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CalendarDays size={20} color={COR_MODULO} />
          {/* ⚠️ O título NOMEIA o assunto quando o calendário é de um programa
              só. Sem isso a tela mostra "0 atrasados" e se lê como "nada
              pendente na manutenção", quando o pendente está no outro programa,
              na outra frente do hub. */}
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
            {tipoFixo ? `Programação — ${TIPO_VISUAL[tipoFixo].rotulo}` : 'Programação'}
          </h3>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          <button onClick={() => irMes(-1)} style={botaoMes} title="Mês anterior"><ChevronLeft size={16} /></button>
          <span style={{ fontWeight: 700, minWidth: '150px', textAlign: 'center', color: '#0f172a' }}>
            {MESES[mes - 1]} de {ano}
          </span>
          <button onClick={() => irMes(1)} style={botaoMes} title="Próximo mês"><ChevronRight size={16} /></button>

          <select
            value={loja}
            onChange={(e) => setLoja(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: '170px', fontFamily: FONTE }}
            title="Visão macro (rede) ou micro (uma loja)"
          >
            <option value="">Todas as lojas (macro)</option>
            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>

          {podeGerir && (
            <button
              onClick={abrirNovo}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', border: 'none',
                background: COR_MODULO, color: '#fff', fontWeight: 700,
                padding: '9px 14px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONTE,
              }}
            >
              <Plus size={16} /> Agendar
            </button>
          )}
        </div>
      </div>

      {/* ---------------- Placar do mês + legenda das cores ----------------
          ⚠️ São DUAS leituras na mesma faixa e a ordem importa: primeiro o
          placar por situação (quantos, e o atraso em vermelho), depois a legenda
          das cores DO GRID, que passaram a ser por tipo. Sem a segunda metade a
          faixa se leria como legenda das tags — e as cores não batem mais. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px',
        alignItems: 'center',
      }}>
        {ORDEM_SITUACAO.map((s) => {
          const v = SITUACAO_VISUAL[s];
          return (
            <div key={s} style={{
              display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: v.fundo,
              color: v.cor, borderRadius: '999px', padding: '4px 12px', fontSize: '12px', fontWeight: 700,
            }}>
              <span>{v.rotulo}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{placar[s] || 0}</span>
            </div>
          );
        })}

        {/* A legenda de TIPO só existe quando os dois assuntos dividem o grid.
            No calendário de um programa só, ela seria uma pastilha única
            repetindo o título — e as tags do grid já voltaram a ser da situação,
            ou seja, as pastilhas acima voltaram a ser a legenda delas. */}
        {!tipoFixo && (
          <>
            {/* Separador para o olho não ler as duas leituras como uma fila só. */}
            <span style={{ width: '1px', alignSelf: 'stretch', backgroundColor: '#e2e8f0', margin: '0 4px' }} />

            {ORDEM_TIPO.map((t) => {
              const v = TIPO_VISUAL[t];
              const Icone = v.icone;
              return (
                <div key={t} style={{
                  display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: v.cor,
                  color: '#ffffff', borderRadius: '999px', padding: '4px 12px', fontSize: '12px', fontWeight: 700,
                }}>
                  <Icone size={13} /> <span>{v.rotulo}</span>
                </div>
              );
            })}
          </>
        )}
      </div>

      {erro && (
        <div style={{
          backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '8px',
          padding: '10px 14px', marginBottom: '12px', fontSize: '13px', fontWeight: 600,
        }}>{erro}</div>
      )}

      {carregando
        ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Carregando a programação…</div>
        : <GridMes ano={ano} mes={mes} renderDia={renderDia} alturaCelula="118px" />}

      {!carregando && !itens.length && (
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '13px', marginTop: '14px' }}>
          {tipoFixo === 'Certificacao' && 'Nenhuma certificação de equipamento programada para '}
          {tipoFixo === 'Conservacao' && 'Nenhuma conservação predial programada para '}
          {!tipoFixo && 'Nenhuma certificação ou conservação programada para '}
          {MESES[mes - 1].toLowerCase()}{loja ? ` na ${loja}` : ''}.
        </p>
      )}

      {/* ---------------- Passivo: vencidos de meses anteriores ----------------
          ⚠️ Este bloco NÃO segue a navegação de mês — de propósito. Ele é o que
          impede a tela de mentir por omissão: sem ele, quem abre setembro lê
          "0 atrasados" e entende "nada pendente na rede", enquanto o serviço
          de março segue aberto. Vem ANTES do resumo do mês porque dívida
          antiga cobra primeiro. */}
      {!carregando && !!atrasadosAnteriores.length && (
        <div style={{
          marginTop: '28px', border: '1px solid #fecaca', borderLeft: '4px solid #b91c1c',
          borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff',
        }}>
          <div style={{ padding: '10px 14px', backgroundColor: '#fee2e2' }}>
            <strong style={{ color: '#b91c1c', fontSize: '13px' }}>
              Atrasados de períodos anteriores
            </strong>
            <span style={{
              color: '#b91c1c', fontSize: '12px', fontWeight: 700, marginLeft: '8px',
              fontVariantNumeric: 'tabular-nums', opacity: 0.85,
            }}>
              {atrasadosAnteriores.length} {atrasadosAnteriores.length === 1 ? 'item' : 'itens'}
            </span>
            <div style={{ color: '#991b1b', fontSize: '12px', marginTop: '2px' }}>
              Venceram antes de {MESES[mes - 1].toLowerCase()} e seguem sem relatório.
              Não dependem do mês exibido.
            </div>
          </div>
          <div>
            {atrasadosAnteriores.map((it, i) => (
              <LinhaResumo
                key={it.ID} item={it} cor="#b91c1c"
                rotuloAssunto={rotuloAssunto} primeira={!i}
                onClick={() => setDetalhe(it)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ---------------- Resumo abaixo do calendário ----------------
          O grid mostra QUANDO; este bloco mostra O QUÊ, por situação. Cada
          agendamento aparece UMA vez aqui (no grid ele ocupa todos os dias do
          período). É a leitura de cobrança: começa pelo que está atrasado. */}
      {!carregando && !!resumo.length && (
        <div style={{ marginTop: '28px', borderTop: '1px solid #e0e7ff', paddingTop: '20px' }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
            Resumo de {MESES[mes - 1].toLowerCase()} de {ano}
            {loja ? ` — ${loja}` : ''}
          </h4>
          {/* ⚠️ O escopo é o MÊS EXIBIDO, igual ao grid acima — e isso precisa
              estar escrito. Um agendamento vencido em março não aparece aqui em
              setembro; quem lê "0 atrasados" sem essa linha entende "nada
              pendente na rede", que é outra afirmação. */}
          <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#64748b' }}>
            Considera apenas o período exibido no calendário.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {resumo.map(({ situacao, itens: doGrupo }) => {
              const visual = SITUACAO_VISUAL[situacao];
              return (
                <div
                  key={situacao}
                  style={{
                    border: '1px solid #e2e8f0', borderLeft: `4px solid ${visual.cor}`,
                    borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff',
                  }}
                >
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 14px', backgroundColor: visual.fundo,
                  }}>
                    <strong style={{ color: visual.cor, fontSize: '13px' }}>{visual.rotulo}</strong>
                    <span style={{
                      color: visual.cor, fontSize: '12px', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums', opacity: 0.85,
                    }}>
                      {doGrupo.length} {doGrupo.length === 1 ? 'item' : 'itens'}
                    </span>
                  </div>

                  <div>
                    {doGrupo.map((it, i) => (
                      <LinhaResumo
                        key={it.ID} item={it} cor={visual.cor}
                        rotuloAssunto={rotuloAssunto} primeira={!i}
                        onClick={() => setDetalhe(it)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------- Tempo por assunto, no ano ----------------
          "Quanto a rede gasta com a Chapa em 2026?" — a leitura que o grid não
          dá. ⚠️ O eixo é ASSIMÉTRICO e isso é o domínio: certificação agrupa por
          EQUIPAMENTO (somando as lojas — o equipamento é o assunto), conservação
          agrupa por LOJA (o objeto é o prédio, e o prédio é a loja).
          ⚠️ É do ANO, não do mês: não muda ao navegar. */}
      {!carregando && (!!tempos.certificacao.length || !!tempos.conservacao.length) && (
        <div style={{ marginTop: '28px', borderTop: '1px solid #e0e7ff', paddingTop: '20px' }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
            Tempo programado em {ano}{loja ? ` — ${loja}` : ''}
          </h4>
          <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#64748b' }}>
            Soma dos dias de cada período do ano inteiro — não muda ao navegar entre os meses.
            O período conta inteiro, incluindo o primeiro e o último dia.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
            {!!tempos.certificacao.length && (
              <div style={{ flex: 1, minWidth: '320px' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px',
                  fontSize: '12px', fontWeight: 800, color: TIPO_VISUAL.Certificacao.cor, textTransform: 'uppercase',
                }}>
                  <ClipboardCheck size={14} /> Certificação, por equipamento
                </div>
                {tempos.certificacao.map((g) => <LinhaTempo key={g.chave} grupo={g} />)}
              </div>
            )}

            {!!tempos.conservacao.length && (
              <div style={{ flex: 1, minWidth: '320px' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px',
                  fontSize: '12px', fontWeight: 800, color: TIPO_VISUAL.Conservacao.cor, textTransform: 'uppercase',
                }}>
                  <Building2 size={14} /> Conservação Predial, por loja
                </div>
                {tempos.conservacao.map((g) => <LinhaTempo key={g.chave} grupo={g} />)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------- Detalhe de um agendamento ---------------- */}
      {detalhe && (() => {
        const visual = SITUACAO_VISUAL[detalhe.situacao] || SITUACAO_VISUAL['Previsto'];
        return (
          <div style={modalOverlay} onClick={() => setDetalhe(null)}>
            <div style={modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={modalHeader}>
                <strong style={{ fontSize: '15px' }}>{detalhe.LOJA} — {rotuloAssunto(detalhe)}</strong>
                <button style={btnX} onClick={() => setDetalhe(null)}><X size={18} /></button>
              </div>
              <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px' }}>
                <div>
                  <span style={{
                    backgroundColor: visual.fundo, color: visual.cor, borderRadius: '999px',
                    padding: '3px 10px', fontSize: '12px', fontWeight: 700,
                  }}>
                    {detalhe.situacao}
                    {/* O atraso é dito em dias: "atrasado" sozinho não mede nada. */}
                    {detalhe.diasAtraso ? ` · ${detalhe.diasAtraso} dia(s)` : ''}
                  </span>
                </div>
                <div><strong>Tipo:</strong> {(TIPO_VISUAL[detalhe.TIPO] || {}).rotulo}</div>
                <div><strong>Período previsto:</strong> {dataBR(soDia(detalhe.DATA_INICIO))} a {dataBR(soDia(detalhe.DATA_FIM))}</div>
                {detalhe.realizacao && (
                  <div>
                    <strong>Realizado em:</strong> {dataBR(detalhe.realizacao.data)}
                    {detalhe.realizacao.rotulo ? ` (${detalhe.realizacao.rotulo})` : ''}
                  </div>
                )}
                {detalhe.OBSERVACAO && <div><strong>Observação:</strong> {detalhe.OBSERVACAO}</div>}
                {detalhe.USUARIO_CRIACAO && (
                  <div style={{ color: '#64748b', fontSize: '12px' }}>Agendado por {detalhe.USUARIO_CRIACAO}</div>
                )}

                {podeGerir && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <button
                      onClick={() => abrirEdicao(detalhe)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${COR_MODULO}`,
                        background: '#fff', color: COR_MODULO, fontWeight: 700, padding: '8px 12px',
                        borderRadius: '8px', cursor: 'pointer', fontFamily: FONTE, fontSize: '13px',
                      }}
                    ><Pencil size={14} /> Editar</button>
                    <button
                      onClick={() => excluir(detalhe)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #fecaca',
                        background: '#fff', color: '#b91c1c', fontWeight: 700, padding: '8px 12px',
                        borderRadius: '8px', cursor: 'pointer', fontFamily: FONTE, fontSize: '13px',
                      }}
                    ><Trash2 size={14} /> Remover</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ---------------- Formulário de agendamento ---------------- */}
      {form && (
        <div style={modalOverlay} onClick={() => !salvando && setForm(null)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <strong style={{ fontSize: '15px' }}>{form.id ? 'Editar programação' : 'Nova programação'}</strong>
              <button style={btnX} onClick={() => setForm(null)}><X size={18} /></button>
            </div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* "O que será feito" só é uma ESCOLHA no calendário dos dois
                  assuntos. No de um programa só ele é dado — mostrado, não
                  editável: agendar o outro programa por aqui criaria uma linha
                  que esta tela nunca exibe. */}
              {tipoFixo ? (
                <div style={rotuloStyle}>
                  O que será feito
                  <div style={{
                    ...inputStyle, backgroundColor: '#f8fafc', color: '#334155',
                    display: 'flex', alignItems: 'center', gap: '6px', fontFamily: FONTE,
                  }}>
                    <IconeTipoFixo size={14} color={TIPO_VISUAL[tipoFixo].cor} />
                    {TIPO_VISUAL[tipoFixo].rotulo}
                  </div>
                </div>
              ) : (
                <label style={rotuloStyle}>
                  O que será feito
                  <select
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    style={{ ...inputStyle, fontFamily: FONTE }}
                  >
                    <option value="Certificacao">Certificação de Equipamento</option>
                    <option value="Conservacao">Conservação Predial</option>
                  </select>
                </label>
              )}

              <label style={rotuloStyle}>
                Loja
                <select
                  value={form.loja}
                  onChange={(e) => setForm({ ...form, loja: e.target.value })}
                  style={{ ...inputStyle, fontFamily: FONTE }}
                >
                  <option value="">Selecione…</option>
                  {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </label>

              {/* Equipamento só existe na Certificação — a Conservação é do prédio. */}
              {form.tipo === 'Certificacao' && (
                <label style={rotuloStyle}>
                  Equipamento
                  <select
                    value={form.idEquipamento}
                    onChange={(e) => setForm({ ...form, idEquipamento: e.target.value })}
                    style={{ ...inputStyle, fontFamily: FONTE }}
                  >
                    <option value="">Selecione…</option>
                    {equipamentos.map((eq) => <option key={eq.ID} value={eq.ID}>{eq.NOME}</option>)}
                  </select>
                </label>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <label style={{ ...rotuloStyle, flex: 1 }}>
                  Início
                  <input
                    type="date" value={form.dataInicio}
                    onChange={(e) => {
                      const dataInicio = e.target.value;
                      // Fim acompanha o início quando ficaria inválido — evita o
                      // 400 do servidor por um campo que a pessoa ainda nem tocou.
                      setForm({ ...form, dataInicio, dataFim: form.dataFim && form.dataFim >= dataInicio ? form.dataFim : dataInicio });
                    }}
                    style={inputStyle}
                  />
                </label>
                <label style={{ ...rotuloStyle, flex: 1 }}>
                  Fim
                  <input
                    type="date" value={form.dataFim} min={form.dataInicio}
                    onChange={(e) => setForm({ ...form, dataFim: e.target.value })}
                    style={inputStyle}
                  />
                </label>
              </div>

              <label style={rotuloStyle}>
                Observação (opcional)
                <textarea
                  value={form.observacao}
                  onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                  rows={3} maxLength={500}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </label>

              {erro && <div style={{ color: '#b91c1c', fontSize: '12px', fontWeight: 600 }}>{erro}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  onClick={() => setForm(null)} disabled={salvando}
                  style={{
                    border: '1px solid #e0e7ff', background: '#fff', color: '#64748b',
                    fontWeight: 700, padding: '9px 14px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONTE,
                  }}
                >Cancelar</button>
                <button
                  onClick={salvar} disabled={salvando}
                  style={{
                    border: 'none', background: COR_MODULO, color: '#fff', fontWeight: 700,
                    padding: '9px 16px', borderRadius: '8px',
                    cursor: salvando ? 'not-allowed' : 'pointer', opacity: salvando ? 0.7 : 1, fontFamily: FONTE,
                  }}
                >{salvando ? 'Salvando…' : 'Salvar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarioManutencao;
